// src/components/OnboardingTour.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';

const OnboardingTour = ({ user, active, onClose, setCurrentTab }) => {
  const [stepIndex, setStepIndex] = useState(0);
  const [coords, setCoords] = useState({ top: 0, left: 0, width: 0, height: 0 });
  const tooltipRef = useRef(null);

  const steps = [
    {
      target: '.sidebar-logo',
      title: 'Welcome to GTELINQ Onboarding!',
      content: 'Let’s take a 1-minute guided tour of your onboarding journey. We will show you the exact sequence of steps to complete.',
      placement: 'right'
    },
    {
      target: '.sidebar-item:nth-child(2)',
      title: 'Step 1: Personal Information',
      content: 'Start here! Enter your profile details, pronouns, citizenship, and residential address.',
      placement: 'right',
      tab: 'personal'
    },
    {
      target: '.sidebar-item:nth-child(3)',
      title: 'Step 2: Emergency Contacts',
      content: 'Add primary and secondary contact details for emergencies.',
      placement: 'right',
      tab: 'contacts'
    },
    {
      target: '.sidebar-item:nth-child(4)',
      title: 'Step 3: US Identification / Documents',
      content: 'Upload photos/scans of your government documents (Passport, Driver License, etc.).',
      placement: 'right',
      tab: 'documents'
    },
    {
      target: '.sidebar-item:nth-child(5)',
      title: 'Step 4: Offer Letter Acceptance',
      content: 'Read your official Job Offer Letter and electronically sign it. The signed copy will be stored in our database.',
      placement: 'right',
      tab: 'offer'
    },
    {
      target: '.sidebar-item:nth-child(6)',
      title: 'Step 5: Employee Agreement',
      content: 'Review and sign your corporate Employment Agreement and NDAs.',
      placement: 'right',
      tab: 'agreement'
    },
    {
      target: '.sidebar-item:nth-child(7)',
      title: 'Step 6: Company Policies',
      content: 'Read and sign acceptance for our 5 standard corporate policies (Attendance, Handbook, Security, etc.).',
      placement: 'right',
      tab: 'policies'
    },
    {
      target: '.sidebar-item:nth-child(8)',
      title: 'Step 7: Bank Details',
      content: 'Configure your payroll routing and account information for direct deposits.',
      placement: 'right',
      tab: 'bank'
    },
    {
      target: '.sidebar-item:nth-child(9)',
      title: 'Step 8: Badge Headshot',
      content: 'Upload a formal headshot for your official security badge print.',
      placement: 'right',
      tab: 'photo'
    },
    {
      target: '.sidebar-item:nth-child(10)',
      title: 'Step 9: Review & Submit',
      content: 'Review your complete documentation and submit for final HR approval.',
      placement: 'right',
      tab: 'submit'
    }
  ];

  const currentStep = steps[stepIndex];

  const updatePosition = useCallback(() => {
    const el = document.querySelector(currentStep.target);
    if (el) {
      const rect = el.getBoundingClientRect();
      setCoords({
        top: rect.top + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width,
        height: rect.height
      });
      
      if (currentStep.tab) {
        setCurrentTab(currentStep.tab);
      } else {
        setCurrentTab('dashboard');
      }
    }
  }, [currentStep.target, currentStep.tab, setCurrentTab]);

  useEffect(() => {
    if (!active) return;

    // Calculate after rendering has finished
    const timeout = setTimeout(updatePosition, 150);
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition);
    return () => {
      clearTimeout(timeout);
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition);
    };
  }, [stepIndex, active, updatePosition]);

  if (!active) return null;

  const handleNext = () => {
    if (stepIndex < steps.length - 1) {
      setStepIndex(stepIndex + 1);
    } else {
      handleFinish();
    }
  };

  const handleBack = () => {
    if (stepIndex > 0) {
      setStepIndex(stepIndex - 1);
    }
  };

  const handleFinish = () => {
    localStorage.setItem(`onboarding_tour_seen_${user.id}`, 'true');
    setCurrentTab('personal'); // Start them at step 1
    onClose();
  };

  const handleSkip = () => {
    localStorage.setItem(`onboarding_tour_seen_${user.id}`, 'true');
    setCurrentTab('dashboard'); // Return to dashboard
    onClose();
  };

  // Tooltip positioning style
  const tooltipStyle = {
    position: 'absolute',
    top: coords.top + (coords.height / 2) - 80,
    left: coords.left + coords.width + 25,
    zIndex: 10002,
  };

  const arrowStyle = {
    position: 'absolute',
    top: coords.top + (coords.height / 2) - 10,
    left: coords.left + coords.width + 10,
    width: 0,
    height: 0,
    borderTop: '10px solid transparent',
    borderBottom: '10px solid transparent',
    borderRight: '15px solid var(--primary)',
    zIndex: 10002,
    animation: 'arrowPulse 1.2s infinite'
  };

  const highlightStyle = {
    position: 'absolute',
    top: coords.top - 5,
    left: coords.left - 5,
    width: coords.width + 10,
    height: coords.height + 10,
    borderRadius: '6px',
    border: '3px solid var(--primary)',
    boxShadow: '0 0 15px var(--primary), 0 0 0 9999px rgba(15, 23, 42, 0.65)',
    zIndex: 10001,
    pointerEvents: 'none',
    transition: 'all 0.3s ease'
  };

  return (
    <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 10000 }}>
      {/* Background Dim Cutout */}
      <div style={highlightStyle} />

      {/* Pulsing pointer arrow */}
      <div style={arrowStyle} />

      {/* Floating Tooltip Panel */}
      <div 
        ref={tooltipRef}
        className="tour-tooltip"
        style={tooltipStyle}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: '0.75rem', fontWeight: 800, color: 'var(--primary)', textTransform: 'uppercase' }}>
            Tour Guide (Step {stepIndex + 1}/{steps.length})
          </span>
          <button 
            type="button"
            onClick={handleSkip} 
            style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', fontSize: '0.75rem', cursor: 'pointer', fontWeight: 600 }}
          >
            Skip Tour
          </button>
        </div>

        <h4 style={{ margin: '0.25rem 0 0.5rem 0', fontSize: '1.05rem', fontWeight: 800, color: 'var(--text-primary)' }}>
          {currentStep.title}
        </h4>
        <p style={{ margin: 0, fontSize: '0.825rem', color: 'var(--text-secondary)', lineHeight: '1.4' }}>
          {currentStep.content}
        </p>

        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '0.75rem', gap: '0.5rem' }}>
          <button 
            type="button"
            onClick={handleBack} 
            disabled={stepIndex === 0}
            className="btn btn-secondary"
            style={{ padding: '0.4rem 0.8rem', fontSize: '0.75rem', flex: 1 }}
          >
            ← Back
          </button>
          <button 
            type="button"
            onClick={handleNext} 
            className="btn btn-primary"
            style={{ padding: '0.4rem 0.8rem', fontSize: '0.75rem', flex: 1 }}
          >
            {stepIndex === steps.length - 1 ? 'Start Onboarding! 🚀' : 'Next →'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default OnboardingTour;
